// components/UserPickerDrawer.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  HStack,
  Text,
  Spinner,
  useToast,
  Tag,
  TagLabel,
  Flex,
  Divider,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useCognito } from "../utils/useCognito";
import axios from "axios";

const API_GET_USERS =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getUser";

const normalize = (str) => (str || "").toLowerCase();

export default function UserPickerDrawer({ isOpen, onClose, onSelectUser }) {
  const { getIdToken } = useCognito();
  const toast = useToast();

  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const token = getIdToken();
        const res = await axios.post(
          API_GET_USERS,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setUsers(res.data.users || []);
      } catch (err) {
        console.error("Error fetching users for picker:", err);
        toast({
          title: "Failed to load users",
          description: "Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [isOpen, getIdToken, toast]);

  const filteredUsers = useMemo(() => {
    const q = normalize(search);
    if (!q) return users;

    return users.filter((u) => {
      const name = `${u.FirstName || ""} ${u.LastName || ""}`;
      return (
        normalize(name).includes(q) ||
        normalize(u.email).includes(q) ||
        normalize(u.UserId).includes(q)
      );
    });
  }, [search, users]);

  const handleSelect = (user) => {
    if (!user) return;
    onSelectUser?.(user);
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          Select a User
          <Text fontSize="sm" color="gray.500" mt={1}>
            Choose the account this invoice should be linked to. We’ll handle
            the IDs for you.
          </Text>
        </DrawerHeader>

        <DrawerBody pt={4}>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search by name, email, or User ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              borderRadius="xl"
            />
          </InputGroup>

          {isLoading ? (
            <Flex mt={6} align="center" justify="center">
              <Spinner size="lg" />
            </Flex>
          ) : (
            <Box
              mt={4}
              maxH="calc(100vh - 220px)" // leave room for header + search
              overflowY="auto"
              borderRadius="lg"
              borderWidth="1px"
              borderColor="gray.700"
              p={2}
            >
              {filteredUsers.length === 0 ? (
                <Flex align="center" justify="center" h="100%">
                  <Text color="gray.500">No users match this search.</Text>
                </Flex>
              ) : (
                <VStack align="stretch" spacing={2}>
                  {filteredUsers.map((user) => {
                    const fullName = `${user.FirstName || ""} ${
                      user.LastName || ""
                    }`.trim();
                    const hasName = !!fullName;
                    const facilityName =
                      user.facilityName ||
                      user.FacilityName ||
                      user.BAAFacilityName ||
                      "";

                    return (
                      <Box
                        key={user.UserId}
                        borderRadius="lg"
                        borderWidth="1px"
                        borderColor="gray.700"
                        p={3}
                        cursor="pointer"
                        _hover={{
                          bg: "teal.50",
                          borderColor: "teal.400",
                        }}
                        transition="background-color 0.12s ease-out, border-color 0.12s ease-out"
                        onClick={() => handleSelect(user)}
                      >
                        <HStack justify="space-between" align="flex-start">
                          <VStack align="flex-start" spacing={0.5}>
                            <Text fontWeight="semibold">
                              {hasName ? fullName : "(No name on file)"}
                            </Text>
                            <Text fontSize="sm" color="gray.300">
                              {user.email}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              User ID:{" "}
                              <Text
                                as="span"
                                fontFamily="mono"
                                fontSize="xs"
                                color="gray.200"
                              >
                                {user.UserId}
                              </Text>
                            </Text>
                          </VStack>
                          <VStack align="flex-end" spacing={1}>
                            {facilityName && (
                              <Tag size="sm" variant="subtle" colorScheme="teal">
                                <TagLabel>{facilityName}</TagLabel>
                              </Tag>
                            )}
                            {user.baaStatus && (
                              <Tag
                                size="sm"
                                variant="subtle"
                                colorScheme={
                                  user.baaStatus === true ||
                                  user.baaStatus === "true"
                                    ? "green"
                                    : "yellow"
                                }
                              >
                                <TagLabel>
                                  BAA:{" "}
                                  {user.baaStatus === true ||
                                  user.baaStatus === "true"
                                    ? "Signed"
                                    : String(user.baaStatus)}
                                </TagLabel>
                              </Tag>
                            )}
                          </VStack>
                        </HStack>

                        <Divider my={2} opacity={0.2} />

                        <Text fontSize="xs" color="gray.500">
                          Click to link this invoice to{" "}
                          <Text
                            as="span"
                            fontFamily="mono"
                            fontSize="xs"
                            color="teal.300"
                          >
                            {user.email}
                          </Text>
                          .
                        </Text>
                      </Box>
                    );
                  })}
                </VStack>
              )}
            </Box>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
